// Lightweight admin auth helper — JWT in sessionStorage.
// Used by /admin/voices and /admin/random-acts pages.

import axios from "axios";

const STORAGE_KEY = "docm.admin.token.v2";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const adminAuth = {
  getToken() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  },
  setToken(token) {
    try {
      sessionStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* ignore quota */
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
  authHeader() {
    const t = this.getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  },
  /**
   * Decode the `sub` claim (admin email) out of the stored JWT without
   * hitting the network. Falls back to empty string if the token is
   * missing / malformed. Added 2026-07-26 so admin queue pages can show
   * the signed-in email in the header without a `/auth/me` round-trip.
   */
  email() {
    const t = this.getToken();
    if (!t) return "";
    try {
      const [, payloadB64] = t.split(".");
      if (!payloadB64) return "";
      // Base64URL → base64
      const b64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/");
      // Pad if needed
      const pad = "=".repeat((4 - (b64.length % 4)) % 4);
      const json = atob(b64 + pad);
      const claims = JSON.parse(json);
      return claims?.sub || claims?.email || "";
    } catch {
      return "";
    }
  },
  async login(email, password) {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data?.token) this.setToken(data.token);
    return data;
  },
  async me() {
    const { data } = await axios.get(`${API}/auth/me`, { headers: this.authHeader() });
    return data;
  },
  async logout() {
    // Fire-and-forget — the real action is clearing the local token.
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: this.authHeader() });
    } catch {
      /* server may already consider us logged out — ignore */
    }
    this.clear();
  },
};

// Convenience: named export mirroring adminAuth.authHeader() so pages
// can do `import { adminHeaders } from "@/lib/adminAuth"` without
// destructuring the singleton. Added 2026-07-16 for AdminLuminaries.
export function adminHeaders() {
  return adminAuth.authHeader();
}

// Map FastAPI's varied `detail` shapes (string | array | dict) to a single
// readable string. Required because rendering a raw object in JSX crashes React.
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
